// Telefonla sipariş (PhoneOrder) ve telefon müşteri dizini (PhoneCustomer) tipleri.
// Dizin, mutfacik.com kayıtlı kullanıcılarından ayrıdır: arkasında hesap/giriş yok.
// Telefon siparişi panelin mevcut sipariş listesinde (Order/GetOrderList) görünmez.

export const PHONE_MAX_LINES = 100;
export const PHONE_MAX_QUANTITY = 100;
export const PHONE_MAX_NAME = 100;
export const PHONE_MAX_PHONE_INPUT = 20;
export const PHONE_MAX_STREET = 200;
export const PHONE_MAX_ADDRESS_DETAIL = 500;
export const PHONE_MAX_NOTE = 500;
export const PHONE_MIN_SEARCH_LENGTH = 3;
export const PHONE_LEDGER_MAX_WINDOW_DAYS = 92;
export const PHONE_LEDGER_DEFAULT_PAGE_SIZE = 20;

// Telefon siparişinin izinli kümesi salonunkinden FARKLI: salonda ...894 (Kart),
// telefonda ...893 (Kapıda Kredi Kartı). Salonun kartı burada PaymentMethodNotAllowed alir.
// Etiket id uzerinden yazilir; lookup adlari panelden degistirilebiliyor, ad yalniz yedek.
export const PHONE_PAYMENT_METHODS = [
  { id: "a1b2c3d4-e5f6-7890-abcd-ef1234567892", label: "Nakit", icon: "bi-cash-stack" },
  { id: "a1b2c3d4-e5f6-7890-abcd-ef1234567893", label: "Kapıda Kredi Kartı", icon: "bi-credit-card-2-front-fill" },
] as const;

// "Belirlenmedi" satiri gonderilebilecek bir deger degil, alanin yokluğu.
export const PHONE_PAYMENT_UNSPECIFIED_ID = "a1b2c3d4-e5f6-7890-abcd-ef1234567895";

export function phonePaymentLabel(paymentMethodId: string, fallbackName?: string | null): string {
  const id = (paymentMethodId ?? "").toLowerCase();
  const known = PHONE_PAYMENT_METHODS.find((m) => m.id === id);
  if (known) return known.label;
  if (id === PHONE_PAYMENT_UNSPECIFIED_ID) return "Belirlenmedi";
  return fallbackName ?? "Belirlenmedi";
}

// Durum adlarinin sonunda nokta var ("Hazırlanıyor."); karsilastirma Guid uzerinden yapilir.
export const PHONE_ORDER_STATUS = {
  preparing: "700924e1-a720-4dbe-8374-6ae56adc4346",
  onTheWay: "840e3442-8a36-4404-8e4b-f6d1fafbbf3b",
  delivered: "55b08d65-d81a-4712-8332-8e59e8340940",
  cancelled: "950565f2-baa3-4977-bef8-8e1dc5962123",
} as const;

// Sunucudaki OrderStatusFlow ile aynı: tek yönlü; iptal yalnız hazırlanırken (ayrıca ele alınır).
export const PHONE_ORDER_NEXT_STEP: Record<string, { to: string; label: string; icon: string }> = {
  [PHONE_ORDER_STATUS.preparing]: { to: PHONE_ORDER_STATUS.onTheWay, label: "Yola Çıktı", icon: "bi-scooter" },
  [PHONE_ORDER_STATUS.onTheWay]: { to: PHONE_ORDER_STATUS.delivered, label: "Teslim Edildi", icon: "bi-house-check-fill" },
};

/* ---------------------------------------------------------- müşteri dizini */

export interface IPhoneCustomer {
  id: string;
  restourantId: string;
  firstName: string;
  lastName: string;
  /** Her zaman normalleşmiş: +90 ve 10 hane. Operatörün yazdığı biçim saklanmaz. */
  phoneNumber: string;
  cityId: string;
  cityName: string;
  townId: string;
  townName: string;
  streetName: string;
  addressDetail: string;
  createdAtUtc: string;
  updatedAtUtc: string | null;
}

export interface IPhoneCustomerSearchResult {
  items: IPhoneCustomer[];
  /** Sunucudan gelir ki ekran sinirlari koda gömmesin. */
  resultLimit: number;
  /** Doğruysa daha fazla harf yazilmasi istenmeli; sayfalama yok. */
  isLimitReached: boolean;
}

export interface ICreatePhoneCustomer {
  restourantId: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  cityId: string;
  townId: string;
  streetName: string;
  addressDetail: string;
}

/** Govdede restourantId YOK: restoran kayitli satirdan okunur. Yama degil, degistirme. */
export interface IUpdatePhoneCustomer {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  cityId: string;
  townId: string;
  streetName: string;
  addressDetail: string;
}

/* ------------------------------------------------------------ sipariş alma */

export type PhoneDirectoryOutcome = "NotSaved" | "Created" | "Updated";

/**
 * Adres alanlarinin hepsi her durumda zorunlu, phoneCustomerId secilmis olsa bile:
 * siparise donan deger her zaman istekteki degerdir, dizin sonradan duzeltilse de
 * gecmis siparis degismez.
 */
export interface IPhoneOrderDelivery {
  phoneCustomerId?: string;
  /** Varsayilan false. */
  saveToDirectory?: boolean;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  cityId: string;
  townId: string;
  streetName: string;
  addressDetail: string;
}

export interface IPhoneOrderNewUpgrade {
  menuUpgradeId: string;
  quantity: number;
}

export interface IPhoneOrderNewLine {
  foodId: string;
  quantity: number;
  foodOrderNote?: string;
  upgrades?: IPhoneOrderNewUpgrade[];
}

export interface ICreatePhoneOrder {
  restourantId: string;
  delivery: IPhoneOrderDelivery;
  lines: IPhoneOrderNewLine[];
  /** Opsiyonel; verilmezse siparis Belirlenmedi ile dogar ve sonradan duzeltilir. */
  paymentMethodId?: string;
  orderNote?: string;
}

export interface IPhoneOrderResultUpgrade {
  menuUpgradeId: string;
  title: string;
  unitPrice: number;
  quantity: number;
  lineAmount: number;
}

export interface IPhoneOrderResultLine {
  foodId: string;
  foodNameTr: string;
  foodNameEn: string;
  quantity: number;
  unitPrice: number;
  discountedUnitPrice: number;
  foodDiscountId: string | null;
  upgradeTotalAmount: number;
  lineTotal: number;
  foodOrderNote: string | null;
  upgrades: IPhoneOrderResultUpgrade[];
}

export interface IPhoneOrderResultDelivery {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  cityId: string;
  cityName: string;
  townId: string;
  townName: string;
  streetName: string;
  addressDetail: string;
  phoneCustomerId: string | null;
}

export interface IPhoneOrder {
  orderId: string;
  orderCode: string;
  restourantId: string;
  orderDate: string;
  source: "Online" | "Phone" | "DineIn";
  orderStatusId: string;
  orderStatusNameTr: string;
  orderStatusNameEn: string;
  delivery: IPhoneOrderResultDelivery;
  lines: IPhoneOrderResultLine[];
  /** Net tutar, kampanya dusulmus. Ustunden ikinci kez indirim dusulmemeli. */
  totalAmount: number;
  subTotalAmount: number;
  totalDiscountAmount: number;
  currencyId: string;
  currencyName: string;
  currencyDisplayText: string;
  /** Asla null donmez; belirlenmedi icin isPaymentMethodSpecified okunur. */
  paymentMethodId: string;
  paymentMethodNameTr: string;
  paymentMethodNameEn: string;
  isPaymentMethodSpecified: boolean;
  driverId: string | null;
  orderNote: string | null;
  directoryOutcome: PhoneDirectoryOutcome;
}

/* ----------------------------------------------------------------- ledger */

export interface IPhoneOrderLedgerItem {
  orderId: string;
  orderCode: string;
  orderDate: string;
  /** Nicosia saati; ekran kendisi donusturmemeli. */
  orderDateLocal: string;
  customerFullName: string;
  phoneNumber: string;
  deliveryAddress: string;
  contentSummary: string;
  lineCount: number;
  orderNote: string | null;
  totalAmount: number;
  currencyId: string;
  currencyName: string;
  currencyDisplayText: string;
  orderStatusId: string;
  orderStatusNameTr: string;
  orderStatusNameEn: string;
  /** Iki ayri iptal yolu var; bu tek cevaptir. */
  isCancelled: boolean;
  countsTowardRevenue: boolean;
  paymentMethodId: string;
  paymentMethodNameTr: string;
  paymentMethodNameEn: string;
  isPaymentMethodSpecified: boolean;
  hasDriverAssigned: boolean;
  phoneCustomerId: string | null;
}

export interface IPhoneOrderCurrencyTotal {
  currencyId: string;
  currencyName: string;
  currencyDisplayText: string;
  totalAmount: number;
  count: number;
}

export interface IPhoneOrderPaymentBreakdown {
  paymentMethodId: string;
  paymentMethodNameTr: string;
  paymentMethodNameEn: string;
  isUnspecified: boolean;
  currencyId: string;
  currencyName: string;
  currencyDisplayText: string;
  totalAmount: number;
  count: number;
}

/** Sayfanin degil, pencerenin tamaminin ozeti: sayfa degisince degismez. */
export interface IPhoneOrderLedgerSummary {
  orderCount: number;
  revenueOrderCount: number;
  cancelledOrderCount: number;
  totals: IPhoneOrderCurrencyTotal[];
  paymentMethods: IPhoneOrderPaymentBreakdown[];
}

export interface IPhoneOrderLedgerPage {
  restourantId: string;
  from: string;
  to: string;
  fromUtc: string;
  /** Ust uc HARIC. */
  toUtcExclusive: string;
  timeZoneId: string;
  items: IPhoneOrderLedgerItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: IPhoneOrderLedgerSummary;
}
